import { ModalProps } from '@pypestream/design-system';
import { useCallback, useRef, useState } from 'react';

import { ReportBugModal, type ReportBugModalLabels } from './report-bug-modal';

export interface UseReportBugModalOptions {
  /** Which app the report came from, matching the tag `reportError` sets. */
  app: string;
  /** Who to come back to, when the session knows. */
  reporter?: { email?: string; name?: string };
  labels: ReportBugModalLabels;
  /**
   * Whether the channel is open right now. Defaults to `true`.
   *
   * Gates only what has not started yet: while it is `false` the form is not
   * rendered, so it cannot be opened. A form the user already OPENED stays
   * mounted until they close it, even if this flips to `false` meanwhile —
   * unmounting it would throw away the typed message and attached screenshots
   * with no warning, and would leave `isOpen` behind as `true` over a modal
   * that remounts closed. A submit after the channel closed fails inside the
   * form (`submitUserBugReport` refuses without a client) and keeps the draft.
   */
  enabled?: boolean;
}

/**
 * The Report a bug form, ready to drop into a nav.
 *
 * Returns the element rather than rendering it, so the caller decides where it
 * lives in the tree. The channel's own gate goes in through `enabled`, not
 * around the returned element, because only this hook knows whether the form
 * is open and must therefore survive the gate closing.
 */
export const useReportBugModal = ({
  app,
  reporter,
  labels,
  enabled = true,
}: UseReportBugModalOptions) => {
  const modalRef = useRef<ModalProps>(null);
  // The modal binds a document-level paste listener, so it has to KNOW it is
  // open; the design-system modal's own `open` is an imperative flag on a ref
  // and nothing re-renders when it changes.
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => {
    setIsOpen(true);

    if (modalRef.current) modalRef.current.open = true;
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);

    if (modalRef.current) modalRef.current.open = false;
  }, []);

  return {
    openReportBugModal: open,
    reportBugModal:
      enabled || isOpen ? (
        <ReportBugModal
          modalRef={modalRef}
          isOpen={isOpen}
          onClose={close}
          app={app}
          reporter={reporter}
          labels={labels}
        />
      ) : null,
  };
};
